import React, { useContext, useMemo, useState } from 'react';

import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, withStyles } from '@material-ui/core';
import { BiPencil } from 'react-icons/bi';
import { IoCloseOutline } from 'react-icons/io5';
import dayjs from 'dayjs';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { DeviceContext } from '@vyce/core/src/contexts';
import { formatDate, getTime } from '@vyce/core/src/utils/dates';
import { TimeLogByShiftDateItem, TimeLogByShiftDateItemLog } from '@vyce/core/src/types';
import { AppCustomTable } from '@vyce/core/src/components';
import { useBooleanState } from '@vyce/core/src/hooks';
import { monochrome } from '@vyce/core/src/theme/styles';

import { EditSingleTimeLogWorkerDialog } from './EditSingleTimeLogWorkerDialog';
import { TimeLogWarningCell } from './components/TimeLogWarningCell';
import { WorkerHoursCell } from './components/WorkerHoursCell';
import useStyles from './styles';
import { TimeLogSwitcher } from './components/TimeLogSwitcher';

const StyledDialog = withStyles({
  root: {
    //@ts-ignore
    zIndex: '1250 !important',
  },
})(Dialog);

export const ExpandTimeLogsInformation = ({
  timeLogByShift,
  isOpen,
  children,
  userId,
  firstName,
  lastName,
  onClose,
  onSuccess,
  renderAvatar,
  onPreviousWorker,
  onNextWorker,
  onPreviousDate,
  onNextDate,
}: {
  timeLogByShift: TimeLogByShiftDateItem;
  isOpen: boolean;
  children: React.ReactNode;
  userId: string;
  firstName: string;
  lastName: string;
  onClose: () => void;
  onSuccess: () => void;
  renderAvatar: () => React.ReactNode;
  onPreviousWorker?: () => void;
  onNextWorker?: () => void;
  onPreviousDate?: () => void;
  onNextDate?: () => void;
}) => {
  const classes = useStyles();

  const [selectedLog, setSelectedLog] = useState<TimeLogByShiftDateItemLog | null>(null);
  const [isEditDialogOpen, openEditDialogOpen, closeEditDialogOpen] = useBooleanState(false);

  const { isMobile } = useContext(DeviceContext);

  const handleEditClick = (log: TimeLogByShiftDateItemLog) => {
    setSelectedLog(log);
    openEditDialogOpen();
  };
  const handleCloseDialog = () => {
    closeEditDialogOpen();
    setSelectedLog(null);
  };
  const columns = useMemo(
    () => [
      {
        name: 'date',
        title: 'Date',
        flex: 0.3,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box
            display="flex"
            alignItems="center"
            height={70}
            lineHeight="48px"
            onClick={() => handleEditClick(row)}>
            {formatDate(timeLogByShift.date, 'dddd D MMMM')}
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Shift',
        flex: 0.2,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <Box display="flex" alignItems="center" height={70}>
              {row.shift_name}
            </Box>
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Clock In Time',
        flex: 0.2,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <Box display="flex" alignItems="center" style={{ fontWeight: 600 }} height={70}>
              {row.checked_in ? (
                <TimeLogWarningCell check_in_ok={row.check_in_ok} face_in_ok={row.face_in_ok}>
                  <>{getTime(row.checked_in)}</>
                </TimeLogWarningCell>
              ) : (
                '-'
              )}
            </Box>
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Clock Out Time',
        flex: 0.15,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <Box display="flex" alignItems="center" style={{ fontWeight: 600 }} height={70}>
              {row.checked_out ? (
                <TimeLogWarningCell
                  check_out_ok={row.check_out_ok}
                  face_out_ok={row.face_out_ok}
                  auto_clock_out={row.auto_clock_out}>
                  <>{getTime(row.checked_out)}</>
                </TimeLogWarningCell>
              ) : (
                '-'
              )}
            </Box>
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Clocked Hours',
        flex: 0.15,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <WorkerHoursCell decimals={row.clocked_in_hours_decimals} amendmentDecimals={0} />
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Regular Paid Hours',
        flex: 0.2,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <WorkerHoursCell
              decimals={row.basic_hours_decimals}
              amendmentDecimals={row.basic_amendment_hours_decimals}
            />
          </Box>
        ),
      },
      {
        name: 'time_logs',
        title: 'Overtime Paid Hours',
        flex: 0.15,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box onClick={() => handleEditClick(row)}>
            <WorkerHoursCell
              decimals={row.overtime_hours_decimals}
              amendmentDecimals={row.overtime_amendment_hours_decimals}
            />
          </Box>
        ),
      },
      {
        name: 'action',
        title: '',
        flex: 0.15,
        renderCell: (row: TimeLogByShiftDateItemLog) => (
          <Box height="100%" display="flex" alignItems="center" justifyContent="center">
            <Button
              startIcon={<BiPencil size="16px" color={monochrome.mediumdark} />}
              className={classes.buttonWrapper}
              style={{ backgroundColor: monochrome.lighter }}
              onClick={() => handleEditClick(row)}
            />
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <StyledDialog
        container={document.getElementById(MAIN_CONTAINER_ID)}
        fullScreen={isMobile}
        maxWidth="md"
        onClose={onClose}
        open={isOpen}>
        <DialogTitle>
          <Box
            display="flex"
            gridGap={16}
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center">
            <Box>{'Time Log Details'}</Box>

            <Box display="flex" flexWrap="wrap" gridGap={16} alignItems="center">
              {onPreviousWorker && onNextWorker && (
                <TimeLogSwitcher
                  onPrevious={onPreviousWorker}
                  onNext={onNextWorker}
                  text={`${firstName} ${lastName}`}
                />
              )}

              {onPreviousDate && onNextDate && (
                <TimeLogSwitcher
                  onPrevious={onPreviousDate}
                  onNext={onNextDate}
                  minWidth="100px"
                  text={`${dayjs(timeLogByShift.date).format('DD/MM/YY')}`}
                />
              )}

              <IconButton onClick={() => onClose()}>
                <IoCloseOutline size={30} />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent style={{ width: isMobile ? '100%' : 900, height: isMobile ? '100%' : '60vh' }}>
          <Box display="flex" flexDirection="column" gridGap="16px" marginBottom={3}>
            <AppCustomTable data={timeLogByShift.time_logs} columns={columns} />
            {children}
          </Box>
        </DialogContent>
      </StyledDialog>

      {selectedLog && (
        <EditSingleTimeLogWorkerDialog
          onSuccess={onSuccess}
          userId={userId}
          log={selectedLog}
          open={isEditDialogOpen}
          handleClose={handleCloseDialog}
          date={timeLogByShift.date}
          renderAvatar={renderAvatar}
        />
      )}
    </Box>
  );
};
